import { memo, useEffect, useMemo, useState } from 'react';
import { Empty, Flex, Spin } from 'antd';
import { Line, ReferenceLine } from 'recharts';
import TimeSeriesLineChart from '@/components/charts/TimeSeriesLineChart';
import { formatNumber, formatTooltipDate, createGranularityXAxisFormatter } from '@/utils';
import type { ChartGranularity, DateRange, EpisodePublishDateBucket } from '@/types';
import { formatBigNumbers } from '@/utils/numberFormattingUtils';
import { DailyMetric, SelectedItemWithColor } from '@/types/content';
import { DateString } from '@/types/state/filter';
import { formatClippedWeekBucketLabel } from './formatClippedWeekBucketLabel';

const TOTAL_LINE_COLOR = '#9CA3AF';
const TOTAL_KEY = 'total';
const CHART_HEIGHT = 300;
const SELECT_ITEMS_BELOW_MESSAGE = 'Select items below to view performance data';

interface PerformanceChartProps {
  selectedItems: SelectedItemWithColor[];
  publishBuckets: EpisodePublishDateBucket[];
  dailyMetrics: ChartDataPoint[];
  granularity: ChartGranularity;
  displayPublishDates: boolean;
  displayTotal: boolean;
  totalDailyMetrics?: DailyMetric[];
  /** Selected filter range, used to clip the weekly tooltip label to a partial first/last week. */
  dateRange?: DateRange;
  /**
   * True while metrics are in flight. Replaces the slot on first load; overlays the
   * existing chart when there is already series data.
   */
  isLoading?: boolean;
}

type ChartDataPoint = {
  date: DateString;
  [id: string]: string | number;
};

interface TooltipContentProps {
  payload: ChartDataPoint;
  selectedItems: SelectedItemWithColor[];
  publishDateMap: Map<string, string[]>;
  displayPublishDates: boolean;
  granularity: ChartGranularity;
  dateRange?: DateRange;
}

const ChartSlot = ({ children }: { children: React.ReactNode }) => (
  <Flex align="center" justify="center" style={{ height: CHART_HEIGHT }}>
    {children}
  </Flex>
);

/**
 * Tooltip content for the performance chart
 * @param payload - The payload of the tooltip
 * @param selectedItems - The selected episodes
 * @param publishDateMap - The publish date map
 * @param displayPublishDates - Whether to show publish dates
 * @param granularity - The granularity
 * @returns The tooltip content
 */
const PerformanceChartTooltipContent = ({
  payload,
  selectedItems,
  publishDateMap,
  displayPublishDates,
  granularity,
  dateRange,
}: TooltipContentProps) => {
  if (!payload?.date) return null;

  const publishedTitles = displayPublishDates ? publishDateMap.get(payload.date) : undefined;

  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium mr-6">
        {granularity === 'week' && dateRange
          ? formatClippedWeekBucketLabel(payload.date, dateRange.from, dateRange.to)
          : formatTooltipDate(payload.date, granularity)}
      </p>
      {publishedTitles && (
        <div className="mb-1 pb-2 border-b border-white/20">
          <p className="text-xs font-medium text-white">Published:</p>
          {granularity === 'day' ? (
            publishedTitles.map((title, i) => (
              <p className="text-xs truncate max-w-[180px]" key={i}>
                {title}
              </p>
            ))
          ) : (
            <p className="text-xs">
              {publishedTitles.length} {publishedTitles.length === 1 ? 'show' : 'shows'} published
            </p>
          )}
        </div>
      )}
      {selectedItems.map((item) => {
        const value = payload[item.id];
        if (value == null) return null;
        const color = item.color;
        return (
          <p className="text-xs inline-flex items-center gap-1" key={item.id}>
            <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: color }} />
            <span className="truncate max-w-[140px]">{item.label}:</span>
            <strong>{formatNumber('en', value as number)}</strong>
          </p>
        );
      })}
      {payload[TOTAL_KEY] != null && (
        <p className="text-xs inline-flex items-center gap-1 mt-1 pt-1 border-t border-white/20">
          <span
            className="w-3 h-3 rounded-full shrink-0"
            style={{ backgroundColor: TOTAL_LINE_COLOR }}
          />
          <span>Total:</span>
          <strong>{formatNumber('en', payload[TOTAL_KEY] as number)}</strong>
        </p>
      )}
    </div>
  );
};

/**
 * Performance chart component
 * @param selectedItems - The selected episodes
 * @param episodesForPublishDates - Episodes published in date range
 * @param dateRange - Filter range
 * @param getColor - Returns the color index for a given episode ID
 * @param dailyMetrics - The daily metrics
 * @param granularity - The granularity
 * @param displayPublishDates - Whether to show publish dates
 * @param displayTotal - Whether to display the total line
 * @param totalDailyMetrics - The total daily metrics
 * @returns The performance chart
 */
const PerformanceChart = memo(
  ({
    selectedItems,
    publishBuckets,
    dailyMetrics,
    granularity,
    displayPublishDates,
    displayTotal,
    totalDailyMetrics,
    dateRange,
    isLoading = false,
  }: PerformanceChartProps) => {
    const [animateOnMount, setAnimateOnMount] = useState(true);

    const lineChartData = useMemo((): ChartDataPoint[] => {
      if (!displayTotal || !totalDailyMetrics?.length) {
        return dailyMetrics;
      }

      const totalsByDate = new Map(
        totalDailyMetrics.map((metric) => [metric.date, metric.downloadsAndViews])
      );

      return dailyMetrics.map((metric) => ({
        ...metric,
        total: totalsByDate.get(metric.date) ?? 0,
      }));
    }, [dailyMetrics, totalDailyMetrics, displayTotal]);

    const publishDateMap = useMemo(() => {
      const map = new Map<string, string[]>();
      for (const bucket of publishBuckets) {
        map.set(bucket.bucketDate, bucket.titles);
      }
      return map;
    }, [publishBuckets]);

    const formatXAxisDate = createGranularityXAxisFormatter(granularity);

    useEffect(() => {
      if (lineChartData.length > 0 && animateOnMount) {
        setAnimateOnMount(false);
      }
    }, [lineChartData, animateOnMount]);

    const baseLineProps = {
      dot: false,
      connectNulls: true,
      isAnimationActive: animateOnMount,
      animationDuration: 500,
      animationBegin: 0,
      animationEasing: 'ease-out' as const,
      strokeWidth: 2,
      type: 'linear' as const,
      fillOpacity: 0,
    };

    const hasChartData = selectedItems.length > 0 && dailyMetrics.length > 0;

    if (!hasChartData) {
      return (
        <ChartSlot>
          {isLoading ? <Spin /> : <Empty description={SELECT_ITEMS_BELOW_MESSAGE} />}
        </ChartSlot>
      );
    }

    return (
      <Spin spinning={isLoading}>
        <TimeSeriesLineChart<ChartDataPoint>
          key="performance-chart"
          data={lineChartData}
          dateKey="date"
          xAxisTickFormatter={formatXAxisDate}
          yAxisFormatter={(value) => (value !== 0 ? formatBigNumbers(value) : '')}
          ariaLabel="Episode performance over time"
          tooltipContent={(payload) => (
            <PerformanceChartTooltipContent
              payload={payload}
              selectedItems={selectedItems}
              publishDateMap={publishDateMap}
              displayPublishDates={displayPublishDates}
              granularity={granularity}
              dateRange={dateRange}
            />
          )}
          height={CHART_HEIGHT}
        >
          {selectedItems.map((item) => (
            <Line
              {...baseLineProps}
              key={`line-${item.id}`}
              name={item.label}
              dataKey={item.id}
              stroke={item.color}
            />
          ))}
          {displayTotal && totalDailyMetrics && (
            <Line
              {...baseLineProps}
              key="line-total"
              className="performance-chart-total-line"
              name="Total"
              dataKey={TOTAL_KEY}
              stroke={TOTAL_LINE_COLOR}
              strokeDasharray="6 3"
            />
          )}
          {displayPublishDates &&
            Array.from(publishDateMap.keys()).map((date) => (
              <ReferenceLine
                key={`publish-${date}`}
                x={date}
                stroke="#4040A8"
                strokeDasharray="4 4"
                strokeWidth={0.5}
              />
            ))}
        </TimeSeriesLineChart>
      </Spin>
    );
  }
);

PerformanceChart.displayName = 'PerformanceChart';

export default PerformanceChart;
